import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface SermonAssets {
  notes?: string;
  confessionImages: string[];
  pointsImages: string[];
  supportsPointOrdering?: boolean;
}

export const useSermonAssets = (sermonId?: string) => {
  return useQuery<SermonAssets>({
    queryKey: ['sermon-assets', sermonId],
    enabled: Boolean(sermonId),
    queryFn: async () => {
      if (!sermonId) return { notes: '', confessionImages: [], pointsImages: [] };
      // 1. Fetch sermon row for notes & confession images
      const { data: sermon, error: sermonErr } = await supabase
        .from('sermons')
        .select('notes, confession_image_urls')
        .eq('id', sermonId)
        .single();
      if (sermonErr) throw sermonErr;

      const confessionImages = (sermon?.confession_image_urls ?? []) as string[];

      // 2. Fetch sermon_documents images (prefer ordered by sort_order when available)
      let supportsPointOrdering = true;
      let docs: any[] = [];

      const orderedQuery = await supabase
        .from('sermon_documents')
        .select('id, file_url, file_type, sort_order, created_at')
        .eq('sermon_id', sermonId);

      if (orderedQuery.error) {
        // Likely missing sort_order column – fall back to basic select so we don't break older schema
        supportsPointOrdering = false;
        const fallback = await supabase
          .from('sermon_documents')
          .select('id, file_url, file_type, created_at')
          .eq('sermon_id', sermonId);
        if (fallback.error) throw fallback.error;
        docs = fallback.data ?? [];
      } else {
        docs = orderedQuery.data ?? [];
      }

      const sortedDocs = [...docs].sort((a, b) => {
        const aOrder = typeof a.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
        const bOrder = typeof b.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        const createdAtCompare = (a.created_at || '').localeCompare(b.created_at || '');
        if (createdAtCompare !== 0) return createdAtCompare;
        return (a.id || '').localeCompare(b.id || '');
      });

      const pointsImages = sortedDocs.filter((d: any) => {
        const mime = (d.file_type || '').toLowerCase();
        return mime.startsWith('image/');
      }).map((d: any) => d.file_url);

      return {
        notes: sermon?.notes || '',
        confessionImages,
        pointsImages,
        supportsPointOrdering,
      } as SermonAssets;
    },
    staleTime: 2 * 60_000,
  });
};
